import { useEffect, useRef, useState } from 'react';

// The resting overlap and the typing-focus reading, shared by SheetOverlay (its
// floor) and IntroCard (restFoot: the guest host card behind a sheet). Moved out
// of SheetOverlay unchanged (signup E3a, v7976 finding (e)).

export const LEAVE_MS = 700;

/**
 * The overlap to rest on (the floor's padding): the last overlap measured with
 * no field focused. When a field lets go, the live reading still holds the
 * leaving keyboard for a few hundred ms — it is ignored until it falls back to
 * (or below) the resting value, or until LEAVE_MS has passed (a Safari bar that
 * changed size meanwhile is then taken up).
 *
 * @param {number} overlap - the live overlap (useViewportOverlap)
 * @param {boolean} typing - a field has focus
 * @returns {number}
 */
const useRestingOverlap = (overlap, typing) => {
  const rest = useRef(typing ? 0 : overlap);
  const wasTyping = useRef(typing);
  const leftAt = useRef(0);
  const [, refresh] = useState(0);
  if (wasTyping.current && !typing) leftAt.current = Date.now();
  wasTyping.current = typing;
  const leaving = !typing && Date.now() - leftAt.current < LEAVE_MS;
  if (!typing && (!leaving || overlap <= rest.current)) rest.current = overlap;
  // Once the leaving window closes, read the live value again even if nothing re-rendered
  useEffect(() => {
    if (!leaving) return undefined;
    const t = setTimeout(() => refresh((n) => n + 1), LEAVE_MS);
    return () => clearTimeout(t);
  }, [leaving]);
  return rest.current;
};

/**
 * A text field has focus — i.e. the keyboard is up.
 * @param {boolean} [enabled=true] - off: always false, nothing attached (an
 *   IntroCard host without restFoot)
 */
export const useTypingFocus = (enabled = true) => {
  const [typing, setTyping] = useState(isTypingTarget(document.activeElement));
  useEffect(() => {
    if (!enabled) return undefined;
    const on = () => setTyping(isTypingTarget(document.activeElement));
    // On focusout the next element isn't focused yet; read after it lands.
    const onOut = () => setTimeout(on, 0);
    document.addEventListener('focusin', on);
    document.addEventListener('focusout', onOut);
    return () => {
      document.removeEventListener('focusin', on);
      document.removeEventListener('focusout', onOut);
    };
  }, [enabled]);
  return enabled && typing;
};
export const isTypingTarget = (el) =>
  !!el && (el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && el.type !== 'button'));

export default useRestingOverlap;
